import api from './client';

export type SignupPlan = 'starter' | 'professional' | 'business';

export type SignupStatus =
  | 'draft'
  | 'awaiting_payment'
  | 'queued_for_provision'
  | 'provisioning'
  | 'live'
  | 'rejected'
  | 'cancelled'
  | 'expired';

export interface SignupIntentPublic {
  id: string;
  status: SignupStatus;
  orgLegalName: string;
  orgTradingName?: string | null;
  country: string;
  timezone: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  subdomain: string;
  plan: SignupPlan;
  flockSizeEstimate?: number | null;
  requestDpa: boolean;
  paymentStatus: string;
  contractsAccepted: boolean;
  legalVersions: { terms: string; privacy: string; aup: string };
  provisionedAppUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  billingConfigured: boolean;
}

export interface SignupIntentAdmin extends SignupIntentPublic {
  adminPhone?: string | null;
  taxNumber?: string | null;
  opsNotes?: string | null;
  provisionedInstanceId?: string | null;
  stripeCustomerId?: string | null;
  acceptedAt?: string | null;
  acceptIp?: string | null;
  legal?: { terms?: string | null; privacy?: string | null; aup?: string | null };
}

export interface CreateSignupIntentPayload {
  orgLegalName: string;
  orgTradingName?: string;
  country: string;
  taxNumber?: string;
  timezone?: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone?: string;
  subdomain: string;
  plan: SignupPlan;
  flockSizeEstimate?: number;
  requestDpa?: boolean;
}

export const signupApi = {
  checkSubdomain: async (name: string) => {
    const { data } = await api.get<{ available: boolean; subdomain: string; reason?: string }>(
      `signup/subdomains/check?name=${encodeURIComponent(name)}`,
    );
    return data;
  },

  createIntent: async (payload: CreateSignupIntentPayload) => {
    const { data } = await api.post<SignupIntentPublic>('signup/intents', payload);
    return data;
  },

  getIntent: async (id: string) => {
    const { data } = await api.get<SignupIntentPublic>(`signup/intents/${id}`);
    return data;
  },

  acceptContracts: async (
    id: string,
    body: {
      acceptTerms: boolean;
      acceptPrivacy: boolean;
      acceptAup: boolean;
      authorisedAttestation: boolean;
    },
  ) => {
    const { data } = await api.post<SignupIntentPublic>(`signup/intents/${id}/contracts`, body);
    return data;
  },

  checkout: async (id: string) => {
    const { data } = await api.post<{ url: string | null; queued: boolean; message?: string }>(
      `signup/intents/${id}/checkout`,
    );
    return data;
  },

  adminQueue: async (status?: string) => {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const { data } = await api.get<SignupIntentAdmin[]>(`signup/admin/queue${q}`);
    return data;
  },

  adminUpdate: async (
    id: string,
    body: {
      status?: SignupStatus;
      opsNotes?: string;
      provisionedInstanceId?: string;
      provisionedAppUrl?: string;
    },
  ) => {
    const { data } = await api.patch<SignupIntentAdmin>(`signup/admin/${id}`, body);
    return data;
  },
};
